/**
 * CropTimeline Component
 *
 * Main timeline orchestrator component for crop schedule visualization.
 * Manages all state, data fetching, and computation for the timeline.
 *
 * This is the entry point for the modular timeline architecture.
 * All UI rendering is delegated to specialized subcomponents.
 */

import React, { useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useOrgParams } from '~/common/use-org-params'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useClimate } from '../hooks/use-climate'
import { useReportsByFarm } from '../hooks/use-reports-by-farm'
import { doyToJSDate } from '~/common/utils/doy-utils'
import { TimelineContextProvider } from '~/crop/crop-schedule/crop-timeline/context/timeline-context-provider'
import { TimelineComputedProvider } from '~/crop/crop-schedule/crop-timeline/context/timeline-computed-provider'
import {
  useTimelineContextDispatch,
  setHoverData,
  setWeekContainerWidth as setWeekContainerWidthAction,
  setContainerWidth,
} from '~/crop/crop-schedule/crop-timeline/context/timeline-context'
import { useTimelineStaticContext } from '~/crop/crop-schedule/crop-timeline/context/timeline-static-context'
import { TimelineContainer } from './components/timeline-container'
import { useTimelineData } from './hooks/use-timeline-data'
import { useTimelineDimensions } from './hooks/use-timeline-dimensions'
import { useTimelineStyles } from '../hooks/use-timeline-styles'
import { useTimelineZoom } from './hooks/use-timeline-zoom'
import { useTimelineScroll } from './hooks/use-timeline-scroll'
import { useIsSeasonReadOnly } from '~/farm/use-is-season-read-only'
import { useEditCropContext } from '~/crop/edit-crop/edit-crop-context'
import {
  calculateTimelineDateRange,
  calculateTotalWeeks,
} from './utils/timeline-date-utils'
import {
  RANGE_START_LFD_OFFSET_MONTHS,
  RANGE_END_FFD_OFFSET_MONTHS,
} from './timeline-constants'

/**
 * CropTimelineInner - Component inside provider that uses context
 */
const CropTimelineInner = ({
  hideYAxis,
  hideRowLines,
  hideToggleButtons,
  forceAllExpand,
  contentHeight,
  interactive,
  onPhaseAdjust,
  onTaskAdjust,
  autoFitZoom,
  scheduleDataOverride,
  dayPrecision,
  fullscreen,
  scrollToLeftOffset,
  scrollToDate,
  scrollTrigger,
  panAnimationX,
  hideBadges,
  printMode,
  children,
}) => {
  const dispatch = useTimelineContextDispatch()
  const {
    weekContainerWidth,
    containerWidth,
    timelineDateRange,
    totalWeeks,
    headerRef,
    yAxisRef,
    contentRef,
    weekWidth,
    groupStrategy,
    sortStrategy,
    expandedIds,
    frostDateRange,
    cutoffDate,
    viewStrategy,
    isDraggingHandleRef,
    showSuccessions,
    showOrgTasks,
  } = useTimelineStaticContext()

  const { farmId } = useOrgParams()
  const { TimelineStyles } = useTimelineStyles()

  // Farm reports for timeline markers
  const { reports: rawReports } = useReportsByFarm({ farmId })
  const reports = useMemo(
    () => rawReports.map(r => ({
      ...r,
      ...TimelineStyles.report(r.report_types?.code),
    })),
    [rawReports, TimelineStyles]
  )

  const editCropCtx = useEditCropContext()

  // Data fetching using reducer state strategies
  const {
    crops,
    groups,
    expandedGroupIds,
    toggleGroup,
    loading,
    successionMap,
  } = useTimelineData({
    scheduleDataOverride,
    dayPrecision,
    timelineDateRange,
    totalWeeks,
    groupStrategy,   // From reducer state
    sortStrategy,    // From reducer state
    viewStrategy,    // From reducer state (hides org task rows in phases-only view)
    showOrgTasks,    // From reducer state (skips org task fetch when off)
  })

  // Patch successionMap to reflect live edit-mode succession count changes
  // (preview children in displayCrops need matching layout space)
  const patchedSuccessionMap = useMemo(() => {
    const editingId = editCropCtx?.editingCropId
    const targetCount = editCropCtx?.successions ?? 0
    if (!editingId || !successionMap) return successionMap
    const existingEntry = successionMap.get(editingId)
    const dbCount = existingEntry?.count ?? 0
    if (targetCount === dbCount) return successionMap
    const patched = new Map(successionMap)
    patched.set(editingId, { ...(existingEntry ?? { children: [] }), count: Math.max(0, targetCount) })
    return patched
  }, [successionMap, editCropCtx?.editingCropId, editCropCtx?.successions])

  // Compute dimensions using groups from data hook + reducer state
  const dimensions = useTimelineDimensions({
    timelineDateRange,
    totalWeeks,
    weekWidth,
    frostDateRange,
    groups,
    expandedIds,
    forceAllExpand,
    cutoffDate,
    viewStrategy,
    successionMap: patchedSuccessionMap,
    showSuccessions,
    reports,
    printMode,
  })

  // Ref so setWeekContainerWidth can read current value without being recreated every render
  const weekContainerWidthRef = useRef(weekContainerWidth)
  useLayoutEffect(() => { weekContainerWidthRef.current = weekContainerWidth }, [weekContainerWidth])

  // Stable setter — never recreated, safe to include in useCallback deps downstream
  const setWeekContainerWidth = useCallback((valueOrUpdater) => {
    const newValue = typeof valueOrUpdater === 'function'
      ? valueOrUpdater(weekContainerWidthRef.current)
      : valueOrUpdater
    dispatch(setWeekContainerWidthAction({ weekContainerWidth: newValue }))
  }, [dispatch])

  // Container width measurement
  useEffect(() => {
    if (!contentRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width
      dispatch(setContainerWidth({ containerWidth: width }))
    })

    resizeObserver.observe(contentRef.current)
    return () => resizeObserver.disconnect()
  }, [contentRef, dispatch])

  // Calculate scrollToX from scrollToDate using timeline's actual week width
  const scrollToX = useMemo(() => {
    if (!scrollToDate || !timelineDateRange || !weekWidth) return null

    // Calculate days from timeline start to target date
    const daysFromStart = Math.floor(
      (scrollToDate - timelineDateRange.start) / (1000 * 60 * 60 * 24)
    )

    // Convert days to pixels using current week width
    return (daysFromStart / 7) * weekWidth
  }, [scrollToDate, timelineDateRange, weekWidth])

  // Zoom hook
  const zoomHook = useTimelineZoom({
    weekContainerWidth,
    totalWeeks,
    containerWidth,
    autoFitZoom,
    contentRef,
    headerRef,
    NOW_X: dimensions.NOW_X,
    setWeekContainerWidth,
    scrollToLeftOffset,
    scrollToX,
    scrollTrigger,
    isDraggingHandleRef,
  })

  // Scroll hook
  const scrollHook = useTimelineScroll({ headerRef, yAxisRef })

  // Mouse tracker for hover line
  const updateTracker = useCallback((e) => {
    const rect = e.currentTarget.closest('svg').getBoundingClientRect()
    const x = e.clientX - rect.left
    const trackerDate = new Date(timelineDateRange.start)
    trackerDate.setDate(timelineDateRange.start.getDate() + (x / weekWidth) * 7)
    dispatch(setHoverData({
      hoverData: {
        x,
        dateStrLong: trackerDate.toDateString(),
        dateStr: trackerDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        show: true
      }
    }))
  }, [timelineDateRange, weekWidth, dispatch])

  // Provide computed values via context (dimensions + zoom/scroll handlers)
  const computedValues = useMemo(() => ({
    ...dimensions,  // TIMELINE_WIDTH, NOW_X, CUTOFF_X, FROST_DATES, getRowY, totalRowsHeight, cropPositions
    handleZoomChange: zoomHook.handleZoomChange,
    minWeekWidth: zoomHook.minWeekWidth,
    maxWeekWidth: zoomHook.maxWeekWidth,
    handleContentScroll: scrollHook.handleContentScroll,
    updateTracker,
    successionMap: patchedSuccessionMap,
    frostDateRange,
    panAnimationX,
  }), [dimensions, zoomHook, scrollHook, updateTracker, patchedSuccessionMap, frostDateRange, panAnimationX])

  return (
    <TimelineComputedProvider computedValues={computedValues}>
      <TimelineContainer
        contentHeight={contentHeight}
        hideYAxis={hideYAxis}
        groups={groups}
        expandedGroupIds={expandedGroupIds}
        toggleGroup={toggleGroup}
        crops={crops}
        interactive={interactive}
        onPhaseAdjust={onPhaseAdjust}
        onTaskAdjust={onTaskAdjust}
        forceAllExpand={forceAllExpand}
        hideRowLines={hideRowLines}
        hideToggleButtons={hideToggleButtons}
        fullscreen={fullscreen}
        loading={loading}
        panAnimationX={panAnimationX}
        hideBadges={hideBadges}
        printMode={printMode}
      >
        {children}
      </TimelineContainer>
    </TimelineComputedProvider>
  )
}

/**
 * CropTimelineWithContext - Main timeline component that provides context
 *
 * Responsibilities:
 * - Fetch and compute timeline data
 * - Provide context with reducer state + computed values
 * - Render layout structure via TimelineContainer
 *
 * Props:
 * @param {boolean} hideYAxis - Hide Y-axis labels
 * @param {boolean} hideRowLines - Hide horizontal grid lines
 * @param {boolean} hideToggleButtons - Hide controls panel
 * @param {boolean} forceAllExpand - Force all crops expanded
 * @param {Array} scheduleData - Override schedule data (for preview mode)
 * @param {number} contentHeight - Fixed content height (px)
 * @param {boolean} interactive - Enable phase drag editing
 * @param {Function} onPhaseAdjust - Phase adjustment callback
 * @param {Function} onTaskAdjust - Task adjustment callback
 * @param {boolean} autoFitZoom - Auto-fit timeline to container width
 * @param {boolean} dayPrecision - Use day precision (default: true)
 * @param {string} viewStrategy - Force view strategy ('phases', 'tasks', 'both')
 * @param {boolean} fullscreen - Remove border and border radius for fullscreen view
 * @param {number} scrollToLeftOffset - Custom scroll offset in pixels (default: 400)
 * @param {Date} scrollToDate - Custom scroll target date (default: NOW)
 * @param {React.ReactNode} children - Custom overlay components (e.g., row helpers)
 */
const CropTimelineWithContext = ({
  hideYAxis = false,
  hideRowLines = false,
  hideToggleButtons = false,
  forceAllExpand = false,
  scheduleData: scheduleDataOverride = null,
  contentHeight = null,
  interactive = false,
  onPhaseAdjust = null,
  onTaskAdjust = null,
  autoFitZoom = false,
  dayPrecision = true,
  viewStrategy = null,
  fullscreen = false,
  scrollToLeftOffset = undefined,
  scrollToDate = undefined,
  scrollTrigger = 0,
  panAnimationX = 0,
  hideBadges = false,
  hideNowLine = false,
  printMode = false,
  initialStateOverride = {},
  children = null,
}) => {
  const { farmId } = useOrgParams()
  const readOnly = useIsSeasonReadOnly()
  const { TimelineStyles } = useTimelineStyles()

  // Refs for synchronized scrolling
  const headerRef = useRef(null)
  const yAxisRef = useRef(null)
  const contentRef = useRef(null)

  // Ref to track whether a phase/task handle drag is in progress
  // Used by useTimelineZoom to suppress scroll updates during dragging
  const isDraggingHandleRef = useRef(false)

  // Task filter cutoff line hover state (from OrgContext)
  const { hoveredTaskGap } = useOrgHoverContext()

  // Calculate cutoffDate from hoveredTaskGap (for timeline preview)
  const cutoffDate = useMemo(() => {
    if (!hoveredTaskGap || hoveredTaskGap === 'all') return null

    const now = new Date()
    const cutoff = new Date(now)

    switch (hoveredTaskGap) {
      case 'now':
        cutoff.setDate(now.getDate())
        break
      case '1w':
        cutoff.setDate(now.getDate() + 7)
        break
      case '2w':
        cutoff.setDate(now.getDate() + 14)
        break
      case '1m':
        cutoff.setMonth(now.getMonth() + 1)
        break
      default:
        return null
    }

    return cutoff
  }, [hoveredTaskGap])

  // Climate dates (frost dates for timeline range calculation)
  const { climateData, error: climateError } = useClimate({ farmId })
  const { avg_lfd = 0, avg_ffd = 365 } = climateData ?? {}

  const frostDateRange = useMemo(() => ({
    lfd: doyToJSDate(avg_lfd),
    ffd: doyToJSDate(avg_ffd),
    color: TimelineStyles.frost.color,
  }), [avg_lfd, avg_ffd, TimelineStyles.frost.color])

  // Timeline date range (6 months before LFD to 3 months after FFD)
  const timelineDateRange = useMemo(() =>
    calculateTimelineDateRange(
      frostDateRange,
      RANGE_START_LFD_OFFSET_MONTHS,
      RANGE_END_FFD_OFFSET_MONTHS
    ),
    [frostDateRange]
  )

  const totalWeeks = useMemo(() =>
    calculateTotalWeeks(timelineDateRange),
    [timelineDateRange]
  )

  // Pass basic computed values to provider (including viewStrategy override if provided)
  const basicComputedValues = useMemo(() => ({
      timelineDateRange,
      totalWeeks,
      frostDateRange,
      cutoffDate,
      headerRef,
      yAxisRef,
      contentRef,
      isDraggingHandleRef,
      forceAllExpand,
      readOnly,
      hideNowLine,
      printMode,
      ...(viewStrategy ? { viewStrategy } : {}),
  }), [timelineDateRange, totalWeeks, frostDateRange, cutoffDate, forceAllExpand, readOnly, viewStrategy, hideNowLine, printMode])

  // Error state
  const error = climateError

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Error loading schedules: {error}</Typography>
      </Box>
    )
  }

  return (
    <TimelineContextProvider value={basicComputedValues} initialStateOverride={initialStateOverride}>
      <CropTimelineInner
        hideYAxis={hideYAxis}
        hideRowLines={hideRowLines}
        hideToggleButtons={hideToggleButtons}
        forceAllExpand={forceAllExpand}
        contentHeight={contentHeight}
        interactive={readOnly ? false : interactive}
        onPhaseAdjust={onPhaseAdjust}
        onTaskAdjust={onTaskAdjust}
        autoFitZoom={autoFitZoom}
        scheduleDataOverride={scheduleDataOverride}
        dayPrecision={dayPrecision}
        fullscreen={fullscreen}
        scrollToLeftOffset={scrollToLeftOffset}
        scrollToDate={scrollToDate}
        scrollTrigger={scrollTrigger}
        panAnimationX={panAnimationX}
        hideBadges={hideBadges}
        printMode={printMode}
      >
        {children}
      </CropTimelineInner>
    </TimelineContextProvider>
  )
}

// Export main component
export const CropTimeline = CropTimelineWithContext

export default CropTimeline
